import {BaseDashlet} from "../../lq_js_common/dashboard/base_dashlet";
import {DashboardGraph} from "../../graphs/dashboard_graph";
import {safeBigIntToNumber} from "../../helpers/number_coerce";
import {isDarkMode} from "../../helpers/dark_mode";
import {debugLog} from '../../helpers/debug_log';
import {redactText} from "../../helpers/redact";
import {
    asnRttToMs,
    colorForMedianRtt,
    formatMedianRtt,
    formatTrafficVolume,
    medianRttStatusLabel,
} from "../../helpers/traffic_overview";

function directionCopy(direction) {
    if (direction === "upload") {
        return {
            title: "Shaper World Traffic Map (Upload)",
            tooltip: "<h5>Shaper World Traffic Map (Upload)</h5><p>Shows where traffic from this shaper is going. Dot size shows selected-period traffic volume and color shows median RTT status.</p>",
            subtitle: "Destinations receiving traffic from this shaper. Larger dots mean more traffic over the selected period. Color shows median RTT.",
            flowLabel: "Traffic leaving this shaper",
        };
    }
    return {
        title: "Shaper World Traffic Map (Download)",
        tooltip: "<h5>Shaper World Traffic Map (Download)</h5><p>Shows where traffic entering this shaper is coming from. Dot size shows selected-period traffic volume and color shows median RTT status.</p>",
        subtitle: "Sources sending traffic into this shaper. Larger dots mean more traffic over the selected period. Color shows median RTT.",
        flowLabel: "Traffic coming into this shaper",
    };
}

export class ShaperAsnMap extends BaseDashlet {
    constructor(slot, direction = "download") {
        super(slot);
        this.rendered = false;
        this.lastGraphState = null;
        this.lastSharedMax = 0;
        this.direction = direction;
    }

    title() {
        return directionCopy(this.direction).title;
    }

    supportsZoom() {
        return true;
    }

    tooltip() {
        return directionCopy(this.direction).tooltip;
    }

    buildContainer() {
        const base = super.buildContainer();
        const note = document.createElement("div");
        note.classList.add("px-3", "pb-2", "small", "text-muted");
        note.textContent = directionCopy(this.direction).subtitle;
        base.appendChild(note);
        base.appendChild(this.graphDiv());
        return base;
    }

    setup() {
        super.setup();
        this.graph = new ShaperAsnMapGraph(this.graphDivId(), this.direction);
        if (this.lastGraphState !== null) {
            this.graph.update(this.lastGraphState, this.lastSharedMax || 0);
        }
    }

    channelRequests() {
        return ["ShaperLocation"];
    }

    onMessage(msg) {
        if (msg.Data.target !== "shaper_location") return;
        debugLog("IM THE MAP", msg.Data);

        let sharedMax = 0;
        for (const row of msg.Data.blob.rows) {
            const down = safeBigIntToNumber(row.bytes_down);
            const up = safeBigIntToNumber(row.bytes_up);
            if (down > sharedMax) sharedMax = down;
            if (up > sharedMax) sharedMax = up;
        }

        const myData = msg.Data.blob.rows.map((row) => {
            const bytes = this.direction === "upload"
                ? safeBigIntToNumber(row.bytes_up)
                : safeBigIntToNumber(row.bytes_down);
            const rttMs = this.direction === "upload"
                ? asnRttToMs(row.rtt_up_median)
                : asnRttToMs(row.rtt_down_median);
            return {
                name: row.country,
                value: [row.longitude, row.latitude, bytes],
                bytes,
                rttMs,
                itemStyle: {
                    color: colorForMedianRtt(rttMs),
                },
            };
        });

        if (!this.rendered) {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("card", "animate__animated", "animate__fadeIn");
        } else {
            const myDiv = document.getElementById(this.id);
            myDiv.classList.add("animate__animated", "animate__pulse");
            setTimeout(() => {
                myDiv.classList.remove("animate__animated", "animate__pulse");
            }, 1000);
        }

        this.graph.update(myData, sharedMax);
        this.lastGraphState = myData;
        this.lastSharedMax = sharedMax;
        this.rendered = true;
    }
}

class ShaperAsnMapGraph extends DashboardGraph {
    constructor(id, direction) {
        super(id);
        this.direction = direction;
        this.currentData = [];
        this.currentSharedMax = 0;
        this.useWebGL = supportsWebGL();

        const dark = isDarkMode();
        if (this.dom) {
            this.dom.style.backgroundColor = dark ? "#000000" : "#ffffff";
        }

        this.option = {
            backgroundColor: dark ? "#000000" : "#ffffff",
            tooltip: {
                trigger: "item",
                formatter: (params) => this.tooltipFormatter(params),
            },
            geo: {
                map: "world",
                roam: false,
                label: {
                    emphasis: {show: false},
                },
                silent: true,
                itemStyle: {
                    normal: {
                        areaColor: dark ? "#14181c" : "#e6eaef",
                        borderColor: dark ? "#0a0a0a" : "#cfd6dd",
                    },
                    emphasis: {
                        areaColor: dark ? "#0f1215" : "#dfe5ea",
                    },
                },
            },
            series: [
                {
                    type: this.useWebGL ? "scatterGL" : "scatter",
                    coordinateSystem: "geo",
                    blendMode: "normal",
                    zlevel: 2,
                    itemStyle: {
                        opacity: 0.9,
                    },
                    symbolSize: [],
                    data: [],
                },
            ],
        };

        this.chart.setOption(this.option);

        window.addEventListener("lqos:redact-changed", () => {
            if (this.currentData.length) {
                this.update(this.currentData, this.currentSharedMax);
            }
        });
    }

    tooltipFormatter(params) {
        const data = params.data || {};
        return `
            <strong>${redactText(data.name || "")}</strong><br/>
            ${directionCopy(this.direction).flowLabel}<br/>
            Volume: ${formatTrafficVolume(data.bytes)}<br/>
            RTT: ${formatMedianRtt(data.rttMs)}<br/>
            Status: ${medianRttStatusLabel(data.rttMs)}
        `;
    }

    update(data, sharedMax) {
        this.chart.hideLoading();
        this.currentData = data;
        this.currentSharedMax = sharedMax;
        this.option.series[0].data = data;

        const maxVal = (typeof sharedMax === "number" && sharedMax > 0) ? sharedMax : 0;
        const minSize = 2;
        const maxSize = 14;
        const computeSize = (value) => {
            let norm = 0;
            if (maxVal > 0) {
                norm = Math.sqrt(value / maxVal);
                if (!Number.isFinite(norm) || norm < 0) norm = 0;
                if (norm > 1) norm = 1;
            }
            return parseInt(minSize + (maxSize - minSize) * norm, 10);
        };

        if (this.useWebGL) {
            this.option.series[0].symbolSize = [];
            for (let i = 0; i < data.length; i++) {
                const value = data[i].bytes || 0;
                this.option.series[0].symbolSize.push(computeSize(value));
            }
            debugLog(this.option.series[0].symbolSize);
        } else {
            this.option.series[0].symbolSize = (value) => {
                const bytes = Array.isArray(value) ? (value[2] || 0) : 0;
                return computeSize(bytes);
            };
        }

        this.chart.setOption(this.option);
    }

    onThemeChange() {
        try {
            const dark = isDarkMode();
            if (this.dom) {
                this.dom.style.backgroundColor = dark ? "#000000" : "#ffffff";
            }
            if (!this.option.geo) this.option.geo = {};
            if (!this.option.geo.itemStyle) this.option.geo.itemStyle = {};
            if (!this.option.geo.itemStyle.normal) this.option.geo.itemStyle.normal = {};
            this.option.backgroundColor = dark ? "#000000" : "#ffffff";
            this.option.geo.itemStyle.normal.areaColor = dark ? "#14181c" : "#e6eaef";
            this.option.geo.itemStyle.normal.borderColor = dark ? "#0a0a0a" : "#cfd6dd";
            if (!this.option.geo.itemStyle.emphasis) this.option.geo.itemStyle.emphasis = {};
            this.option.geo.itemStyle.emphasis.areaColor = dark ? "#0f1215" : "#dfe5ea";
            this.chart.setOption(this.option, true);
        } catch (e) {
            console.warn("onThemeChange error in ShaperAsnMapGraph", e);
        }
    }
}

function supportsWebGL() {
    try {
        const canvas = document.createElement("canvas");
        return !!(window.WebGLRenderingContext && (canvas.getContext("webgl") || canvas.getContext("experimental-webgl")));
    } catch (e) {
        return false;
    }
}
